import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import RunLimitDialog from "./RunLimitDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const PROVIDERS = [
  {
    id: "azure",
    name: "Microsoft Azure",
    color: "#0EA5E9",
    tag: "AZ",
    blurb: "App Service · PostgreSQL · Front Door",
  },
  {
    id: "aws",
    name: "Amazon Web Services",
    color: "#F59E0B",
    tag: "AW",
    blurb: "EC2 · RDS · CloudFront",
  },
  {
    id: "gcp",
    name: "Google Cloud",
    color: "#22C55E",
    tag: "GC",
    blurb: "Cloud Run · Cloud SQL · GKE",
  },
];

export default function TemplateProviderDialog({ template, onClose, onCreated }) {
  const [submitting, setSubmitting] = useState(null);
  const [runLimitOpen, setRunLimitOpen] = useState(false);

  const choose = async (providerId) => {
    if (submitting) return;
    setSubmitting(providerId);
    try {
      const { data: project } = await api.post("/projects/from-template", {
        template_id: template.id,
        provider: providerId,
      });
      toast.success("Template loaded", {
        description: `${project.name} · ${project.architecture.length} services`,
      });
      onCreated(project);
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (status === 429) {
        setRunLimitOpen(true);
      } else {
        toast.error("Couldn't load template", { description: detail });
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <>
    <Dialog open={!!template && !runLimitOpen} onOpenChange={(v) => !v && !runLimitOpen && onClose()}>
      <DialogContent
        style={{
          background: "var(--color-background-secondary)",
          borderColor: "var(--color-border-primary)",
          borderRadius: 16,
          maxWidth: 520,
        }}
        data-testid="template-provider-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-text-primary"
            style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.015em" }}
          >
            Choose a cloud provider
          </DialogTitle>
        </DialogHeader>
        <p
          className="text-text-secondary"
          style={{ fontSize: 13, lineHeight: 1.55 }}
        >
          We&apos;ll load{" "}
          <span className="text-text-primary font-medium">{template?.title}</span> as an editable
          architecture on your selected cloud. Counts as one monthly architecture run.
        </p>

        <div className="grid grid-cols-1 gap-2 mt-1" data-testid="provider-grid">
          {PROVIDERS.map((p) => {
            const loading = submitting === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => choose(p.id)}
                disabled={!!submitting}
                data-testid={`provider-${p.id}`}
                className="motion-micro text-left flex items-center gap-4"
                style={{
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 12,
                  padding: 16,
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting && !loading ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!submitting) e.currentTarget.style.borderColor = p.color;
                }}
                onMouseLeave={(e) => {
                  if (!submitting) e.currentTarget.style.borderColor = "var(--color-border-primary)";
                }}
              >
                <span
                  className="grid place-items-center font-mono shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 9999,
                    background: `${p.color}1F`,
                    border: `1px solid ${p.color}66`,
                    color: p.color,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                  }}
                >
                  {p.tag}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="block text-text-primary"
                    style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.005em" }}
                  >
                    {p.name}
                  </span>
                  <span
                    className="block text-text-tertiary mt-1 truncate"
                    style={{ fontSize: 12 }}
                  >
                    {p.blurb}
                  </span>
                </span>
                <span
                  className="shrink-0 inline-flex items-center"
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: loading ? p.color : "var(--color-text-tertiary)",
                  }}
                >
                  {loading ? "Loading…" : <ArrowRight size={16} strokeWidth={2.25} />}
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
    <RunLimitDialog open={runLimitOpen} onClose={() => setRunLimitOpen(false)} />
    </>
  );
}
